import { icon, named, num, text, toggle, url } from '../../themes/settingFields';
import { element } from './definition';
import { look, paint, radius, round, strip, typeface } from './styleTokens';

// The two parts of a header that belong to the shop rather than to the page: the bag, which knows what
// is in it, and the search, which opens onto the catalog. Both are elements rather than an icon and a
// link because both hold behaviour a merchant cannot get from styles — a live count, and a field that
// opens without leaving the page.

/** A header glyph's target: 40px, which is the tap target both references settled on. */
const TARGET = '40px';

/** The bag, with what the cart holds drawn on it. The count is bound, so nothing here is ever typed. */
export const cartElement = element('cart', {
  label: 'Cart',
  description: 'The bag a shopper opens their order from, showing how many items are in it.',
  category: 'Other',
  icon: 'shopping-bag',
  props: [
    url('href', 'Opens'),
    icon('icon', 'Icon'),
    toggle('showCount', 'Show how many are in it'),
    { ...num('count', 'Items in the cart', 0, 9999), help: 'Read from the cart. Unbind it to draw a fixed number instead.' },
    { ...text('ariaLabel', 'Name for screen readers'), help: 'Read out in place of the bag, e.g. Cart.' }
  ],
  defaults: {
    props: { href: '/cart', icon: 'shopping-bag', showCount: true, count: 0, ariaLabel: 'Cart' },
    bindings: { count: 'cart.count' },
    styles: look({
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
      width: TARGET, height: TARGET, color: paint('heading'), textDecoration: 'none', ...strip('0px')
    })
  }
});

/** The search: a glyph that opens a field, so the catalog is reachable without leaving the page. */
export const searchElement = element('search', {
  label: 'Search',
  description: 'A search glyph that opens a field, so a shopper can look through your catalog.',
  category: 'Other',
  icon: 'search',
  props: [
    { ...named('layout', 'Layout', [['glyph', 'A glyph that opens a search'], ['field', 'A field on the page']]), help: 'A field suits a search results page, holding the words searched for.' },
    icon('icon', 'Icon'),
    text('placeholder', 'Placeholder'),
    url('action', 'Results page'),
    { ...text('ariaLabel', 'Name for screen readers'), help: 'Read out in place of the glyph, e.g. Search.' }
  ],
  defaults: {
    props: { layout: 'glyph', icon: 'search', placeholder: 'Search products', action: '/search', ariaLabel: 'Search' },
    styles: look({
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
      width: TARGET, height: TARGET, color: paint('heading'),
      fontFamily: typeface('body'), fontSize: '16px', ...strip('0px'), ...radius(round('sm'))
    })
  }
});
